import {
  CaseFindingPresentationType,
  DiagnosisOverview,
  QualifiedFeature,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { useEffect, useRef } from "react";
import {
  PatientCase,
  PatientCaseGrouping,
  useDiagnosticAgentDDRequired,
  useDiagnosticAgentState,
} from "../../context/diagnosticAgent";
import { useDiagnosticAgentConfiguration } from "./config/configurationTypes";
import {
  ageFeature,
  caseGroupingToPresentationType,
  genderFeature,
  usePatientCase,
} from "./usePatientCase";
import { v4 as uuid } from "uuid";
import {
  ACTION,
  CATEGORY,
  evt,
  mixpanel_distinct_id,
} from "fe-common/analytics/analytics";
import { patientCaseGroupingForSave } from "./useStoredPatientCase";
import { Relations } from "shared";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { DEFAULT_SORT } from "./hooks/consts";
import { isLocal } from "fe-common/utils/envGetter";
import { useQueryParams } from "fe-common/useQueryParams";
import { getCurrentTime } from "fe-common/utils/time";

export interface useUpdatedDDApi {
  loading: boolean;
  data: DiagnosisOverview;
  clearDD: () => void;
  ddStatus: DDStatusData;
  setLoading: (isLoading: boolean) => void;
}

export enum DDStatus {
  SUCCESS = "SUCCESS",
  INITIAL = "INITIAL",
  ERROR = "ERROR",
  INSUFFICIENT_PATIENT_DATA = "INSUFFICIENT_PATIENT_DATA",
}

export interface DDStatusData {
  status: DDStatus;
  data?: any;
  uuid?: string;
  mixpanelId?: string;
}

const debug = getFeatureFlags("debugSheet") as boolean;

/* eslint-disable */
export function useUpdatedDD(
  pathOptions: any, //<---depricated, not needed
  onDataChange?: (data) => void,
  force?: boolean,
  includeCitations?: boolean,
): useUpdatedDDApi {
  let dao = useDaoContext();

  const queryParams = useQueryParams();

  let [state, setState] = useDiagnosticAgentState();

  let { allFindings, patientCase, setPlanDirty } = usePatientCase();

  let currentSessionId = useRef<string>();

  let { diagnosis: data, diagnosisLoading: loading, ddStatus } = state;

  const [isDDRequired] = useDiagnosticAgentDDRequired();

  let configuration = useDiagnosticAgentConfiguration();

  let consider = configuration.considerPanelIDs;

  const setLoading = (l) =>
    setState((state) => ({
      ...state,
      diagnosisLoading: l,
    }));

  const setData = (data, ddStatusData: DDStatusData) =>
    setState((state) => ({
      ...state,
      diagnosis: data,
      ddStatus: ddStatusData,
      diagnosisLoading: false,
      isLocalRestored: false,
    }));

  const clearDD = () => {
    setData(null, { status: DDStatus.INITIAL });
  };

  useEffect(() => {
    if (!isDDRequired && !force) return;

    if (allFindings == null || allFindings.length === 0 || consider == null) {
      if (state.diagnosis) {
        setData(null, { status: DDStatus.INITIAL });
      }
      state.dirty = false;
      return;
    }
    if (!state.dirty) {
      return;
    } else {
      state.dirty = false;
    }

    let grouped = patientCaseByPresentationType(patientCase);

    setLoading(true);

    currentSessionId.current = uuid();

    let sessionId = currentSessionId.current;

    evt({
      category: CATEGORY.AGENT_DIAGNOSE,
      action: ACTION.DIAGNOSE,
    });

    const groupedFeatures = patientCaseGroupingForSave(patientCase);

    Promise.all([
      dao.getDDWithCase({
        qfs: grouped,
        considerPanels: queryParams.get("dd_panel")
          ? [queryParams.get("dd_panel")]
          : consider,
        diseasesToDiscard: patientCase.diseasesToDiscard,
        diseasesToAdd: patientCase.diseasesToAdd,
        ddOptions: { depth: 3, limit: 20 },
        pathOptions,
        withDebug: debug,
        backEndMode: false,
        sortTypes: [DEFAULT_SORT],
        includeCitations,
      }),
      !isLocal() ? dao.storePatientCase(groupedFeatures, consider, true) : null,
    ])
      .then(([data, shareCaseId]) => {
        if (currentSessionId.current === sessionId) {
          if (data) {
            evt({
              category: CATEGORY.AGENT_DIAGNOSE,
              action: ACTION.SUCCESS,
              label: shareCaseId,
            });
          } else {
            evt({
              category: CATEGORY.AGENT_DIAGNOSE,
              action: ACTION.ERROR,
              label: shareCaseId,
            });
          }
          if (data.clinicalReasoning) {
            // fix up data
            data.clinicalReasoning.sort(
              (a, b) =>
                b.diseaseGivenDependentFindings -
                a.diseaseGivenDependentFindings,
            );
            data.clinicalReasoning = data?.clinicalReasoning.filter(
              (cr) => cr.disease[0].feature,
            );
          }
          setPlanDirty();
          setData(
            data,
            data.allFindings?.length
              ? { status: DDStatus.SUCCESS }
              : { status: DDStatus.INSUFFICIENT_PATIENT_DATA },
          );

          // Make sure other errors don't affect the auto feedback
          try {
            onDataChange && onDataChange(data);
          } catch (e) {
            console.log("Failed to fire onDataChange event!");
            evt({
              category: CATEGORY.AGENT_DIAGNOSE,
              action: ACTION.ERROR,
              label:
                "Failed to fire onDataChange event! - updateDDToState.ts ln.174" +
                e,
            });
          }
        }
      })
      .catch((e: Error) => {
        const feedbackUuid = uuid();
        const errorData =
          e.message +
          "\n" +
          e.name +
          "\nfeedbackId=" +
          feedbackUuid +
          "\ntime=" +
          getCurrentTime() +
          "\n" +
          e.stack +
          "\n";

        evt({
          category: CATEGORY.AGENT_DIAGNOSE,
          action: ACTION.ERROR,
          label: errorData,
        });

        console.error(e);
        setData(null, {
          status: DDStatus.ERROR,
          data: e,
          uuid: feedbackUuid,
          mixpanelId: mixpanel_distinct_id,
        });

        onDataChange && onDataChange(null);
      });
  }, [
    state.dirty,
    allFindings,
    patientCase,
    consider,
    dao,
    pathOptions,
    setState,
    isDDRequired,
    force,
  ]);

  return {
    loading,
    data,
    ddStatus,
    clearDD,
    setLoading,
  };
}

export function patientCaseByPresentationType(patientCase: PatientCase) {
  let grouped: Partial<Record<
    CaseFindingPresentationType,
    QualifiedFeature[]
  >> = {};
  for (let group in PatientCaseGrouping) {
    const caseGroup = PatientCaseGrouping[group];
    const presentationType = caseGroupingToPresentationType(caseGroup);
    const features = patientCase[caseGroup];

    // Check if feature is valid
    // if he has NO qualifiers
    // or every LEVEL relation has a value
    const isValidFeature = (finding: QualifiedFeatureFragment) => {
      return finding.qualifiers.every(
        (q) => q.relation.id !== Relations.LEVEL || q.value,
      );
    };

    const filteredFeatures = features.filter((feature) =>
      isValidFeature(feature),
    );

    grouped[presentationType] = [
      ...(grouped[presentationType] || []),
      ...filteredFeatures,
    ];
  }
  // add demographic if present
  grouped[CaseFindingPresentationType.BackgroundFinding] = [
    ...(grouped[CaseFindingPresentationType.BackgroundFinding] || []),
    ageFeature(patientCase.age),
    genderFeature(patientCase.gender),
  ].filter((elem) => elem != null);

  return grouped;
}
